<template>
    <div class="wrapper__content">
        <div
            v-if="header"
            class="wrapper__header"
        >
            <h2>{{ header }}</h2>
            <slot name="headerRightElement" />
        </div>
        <div
            :class="[
                'wallet',
                {
                    'wallet--with-timer': isTimerShown,
                    'wallet--without-title': !title,
                }
            ]"
        >
            <div :class="['wallet__content', { 'wallet__content--no-sidebar': !isSidebarVisible }]">
                <wallet-risk-warning v-if="showRiskWarning" />
                <div
                    v-if="isTimerShown"
                    class="wallet__timer"
                >
                    <timer
                        :duration="duration"
                        v-on="$listeners"
                    />
                </div>

                <div
                    v-if="title"
                    :class="['wallet__title', {'has-info': info}]"
                >
                    {{ title }}
                </div>

                <p
                    v-if="info"
                    class="wallet__info"
                >
                    {{ info }}
                </p>

                <slot />

                <div
                    v-if="balance"
                    class="wallet__balance"
                >
                    {{ `${$t('buyTokens.walletBalance')} ${balance}` }}
                </div>

                <disclaimer
                    v-if="disclaimerConfig.textList.length"
                    :textList="disclaimerConfig.textList"
                    :isHighlighted="disclaimerConfig.isHighlighted"
                    class="wallet__disclaimer"
                />

                <input-checkbox
                    v-if="termsTextList.length"
                    :textList="termsTextList"
                    noBackground
                    isLabelCentered
                    :maxWidth="false"
                    class="terms-checkbox"
                    @toggleCheckBox="$emit('toggledTerms', $event)"
                />

                <div class="wallet__actions">
                    <p v-if="showChangeMalaysianDeclaration"
                        class="malaysian-declaration"
                        @click="setMalaysianDomesticRinggitBorrowingModal"
                    >
                        {{ $t('jurisdictionRequirements.domesticRinggitBorrowing.changeDeclaration') }}
                    </p>
                    <button v-if="isButtonShow"
                        id="button-proceed"
                        :class="[
                            'wallet__button',
                            {
                                'wallet__button--disabled': isButtonDisabled,
                                'wallet__button--sell': isSellPage,
                                'wallet__button--withdraw' : isWithdrawPage,
                                'wallet__button--centered' : !showChangeMalaysianDeclaration,
                            }
                        ]"
                        @click="proceedButtonAction"
                    >
                        {{ buttonText }}
                    </button>
                </div>

                <div
                    v-if="cancelButtonText"
                    id="button-cancel"
                    :class="[
                        'wallet__button', 'wallet__button--cancel'
                    ]"
                    @click="proceedCancelButtonAction"
                >
                    {{ cancelButtonText }}
                </div>

                <div
                    v-if="isFT"
                    class="info"
                >
                    {{ $t('buyTokensConfirmation.infoFooter') }}
                </div>
            </div>
            <div :class="['wallet__side-content', { 'wallet__side-content--visible': isSidebarVisible }]">
                <slot name="side-content" />
            </div>
        </div>
    </div>
</template>

<script>
// 3rd party libs
import {
    mapGetters,
} from 'vuex';

// Components
import timer from '@webapp/components/timer/timer.vue';
import inputCheckbox from '@webapp/components/inputs/input-checkbox.vue';
import disclaimer from '@webapp/components/disclaimer.vue';
import walletRiskWarning from '@webapp/modules/crypto-and-trading/components/wallet/wallet-risk-warning.vue';

import {
    CountryIso2,
} from '@shared/types/registration';

export default {
    name: 'wallet-form',
    components: {
        timer,
        inputCheckbox,
        disclaimer,
        walletRiskWarning,
    },
    props: {
        header: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        info: {
            type: String,
            default: '',
        },
        balance: {
            type: String,
            default: '',
        },
        isButtonDisabled: {
            type: Boolean,
            default: true,
        },
        isButtonShow: {
            type: Boolean,
            default: false,
        },
        buttonText: {
            type: String,
            default: '',
        },
        cancelButtonText: {
            type: String,
            default: '',
        },
        isTimerShown: {
            type: Boolean,
            default: false,
        },
        isSellPage: {
            type: Boolean,
            default: false,
        },
        isFT: {
            type: Boolean,
            default: false,
        },
        isWithdrawPage: {
            type: Boolean,
            default: false,
        },
        duration: {
            type: Number,
            default: 0,
        },
        isSidebarVisible: {
            type: Boolean,
            default: true,
        },
        showMalaysianDeclarationLabel: {
            type: Boolean,
            default: false,
        },
        disclaimerConfig: {
            type: Object,
            required: false,
            default: () => ({
                textList: [],
                isHighlighted: false,
            }),
        },
        termsTextList: {
            type: Array,
            required: false,
            default: () => ([]),
        },
    },
    computed: {
        ...mapGetters(['userDetails', 'isVatCountryGB']),

        showChangeMalaysianDeclaration() {
            return this.userDetails.vatCountry === CountryIso2.MALAYSIA && this.showMalaysianDeclarationLabel;
        },

        showRiskWarning() {
            return !this.isSellPage && !this.isWithdrawPage && this.isVatCountryGB;
        },
    },
    methods: {
        proceedButtonAction() {
            this.$emit('proceedButtonAction');
        },
        proceedCancelButtonAction() {
            this.$emit('proceedCancelButtonAction');
        },
        setMalaysianDomesticRinggitBorrowingModal() {
            this.$emit('setMalaysianDomesticRinggitBorrowingModal');
        },
    },
};
</script>

<style lang="scss" scoped>
.wallet {
    display: flex;
    text-align: center;
    border-radius: 1rem;
    padding: 4.5rem 2rem 6rem;
    background: $white-color;

    @include min-width(xl) {
        padding: 4.5rem 6.6rem 6rem;
    }

    &--with-timer {
        padding-top: 1.8rem;
    }

    &--without-title {
        margin-top: 2.7rem;
    }

    &__timer {
        align-self: center;
        border-radius: 0.8rem;
        background-color: $cyprus-color;
        width: 4.3rem;
        height: 1.6rem;
        color: $white-color;
        font-size: 1.1rem;
        letter-spacing: 0;
        line-height: 1.7rem;
        margin-bottom: 3rem;
    }

    &__title {
        font-size: 1.8rem;
        line-height: 2.7rem;
        font-weight: 600;
        padding-bottom: 4.5rem;
        color: $cyprus-color;

        &.has-info {
            padding-bottom: 0;
        }
    }

    &__info {
        padding: 1rem 0;
        margin-top: 0;
        color: $red-orange-color;
        font-size: 13px;
    }

    &__content {
        width: 100%;
        display: flex;
        flex-direction: column;

        &:not(&--no-sidebar) {
            @include min-width(xl) {
                width: 60%;
            }
        }
    }

    &__side-info {
        display: none;
        min-width: 30%;
        height: auto;

        @include min-width(lg) {
            display: block;
        }
    }

    &__balance {
        width: calc(100% - 1.7rem);
        text-align: right;
        font-size: 1.2rem;
        color: $cyprus-color;
        position: relative;
        top: -3rem;

        @include min-width(lg) {
            top: -4rem;
            width: calc(100% - 4.7rem);
        }

        @include min-width(xl) {
            top: -3rem;
        }
    }

    &__actions {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
    }

    &__button {
        margin-left: auto;
        background: $orange-peel-color;
        font-size: 1.5rem;
        line-height: 4rem;
        color: $white-color;
        width: 34rem;
        max-width: 100%;
        border-radius: 0.5rem;
        align-self: center;
        margin-top: 1.6rem;
        appearance: none;
        border: 0;
        cursor: pointer;

        &:hover {
            opacity: 0.8;
        }

        &--disabled {
            opacity: 0.5;
            pointer-events: none;
        }

        &--sell,
        &--withdraw {
            background: $blue-ribbon-color;
        }

        &--cancel {
            background: none;
            color: inherit;
        }

        &--centered {
            margin-right: auto;
        }
    }

    &__disclaimer {
        margin-bottom: 1rem;
    }

    &__side-content {
        width: 40%;
        display: none;
        padding-left: 6rem;

        &--visible {
            @include min-width(xl) {
                display: flex;
                flex-direction: column;
            }
        }
    }
}

.terms-checkbox {
    align-self: center;
}

.wrapper {
    &__content {
        padding: 0 2rem;

        @include max-width(sm) {
            padding: 0;
        }
    }

    &__header {
        margin: 0;
        padding: 5rem 0 1.5rem;
        display: flex;
        align-items: center;
        justify-content: space-between;

        @include max-width(sm) {
            padding: 5rem 2rem 1.5rem 2rem;
        }

        h2 {
            color: $cyprus-color;
            font-size: 2rem;
            font-weight: bold;
        }
    }
}

.info {
    text-align: center;
    padding-top: 1.8rem;
    opacity: 0.4;
    color: $cyprus-color;
    font-size: 1.1rem;
    line-height: 1.7rem;

    &--below {
        max-width: 60%;
    }
}

.malaysian-declaration {
    font-size: 1.4rem;
    color: $lima-color;
    font-weight: 700;
    line-height: 2.1rem;
    cursor: pointer;
}
</style>